/* The counterparty card's Comments tab, stage 1: the team's text notes, newest first.

     GET    excalibur/comments/?counterparty=<id>        → {next, previous, results}
     POST   excalibur/comments/  {counterparty, text, images?} → 201, the row
     PATCH  excalibur/comments/:id/  {text, remove_images?} → 200, the row (`modified` moves on)
     DELETE excalibur/comments/:id/                       → 204 (soft)

   A row is `{id, counterparty, text, images, author, created, modified, can_edit}` — every comment
   is a person's note; there are no system events. A comment needs text OR at least one image, and
   that rule is kept HERE, on create and on edit alike — the API no longer checks it: `text` may be
   "" (sent and answered as such), and then the note shows its images alone. `remove_images` is a
   list of this note's image ids to drop (anyone else's are ignored); the answer carries the images
   left. "Edited" is `modified` later than `created` (equal
   on create), compared as dates. `?counterparty=` goes on EVERY list request: without it the API
   answers the whole company's comments, not a 400. A counterparty outside the company (or gone)
   lists empty; on create it is refused under `counterparty`. Any key beyond these two is silently
   ignored (no 400), so nothing else is sent.

   Cursor pagination, 20 a page. `next` is an ABSOLUTE url and `call()` prepends API_URL_PREFIX, so
   it is cut back to the path first (the trap `/tracking` notes). Pages append to the end, rows
   already shown skipped by id — a note added on top, or a row the cursor hands over twice, never
   shows twice. "Load more" only: no loading on scroll. A failed "Load more" keeps what is shown.

   Days are grouped by the date of `created`, read as the browser's local time — the API sends it
   with no zone, in the SERVER's (Europe/Kyiv in production; the local dev API runs UTC, so there a
   new note reads three hours early). Today / Yesterday against the browser's today, other days
   DD.MM.YYYY as every date on the card; inside a day, HH:mm only.

   The new-note draft lives in `Page.js` (`draft` / `setDraft`, and its images: `images` /
   `setImages`), above the tab, so switching tabs keeps it.

   Images are attached to new notes only — an edit can drop a note's images, never add one. They are
   attached by "Attach", by dropping files
   on the composer, or by pasting (a screenshot straight from the clipboard). `util/image-jpeg.js`
   refuses what is not PNG / JPEG / WEBP / GIF, over 10 MB or past 5, each with its reason under the
   composer, and re-encodes the rest as JPEG; the POST carries them as `images: [{image: <data
   URI>}]`. Each draft image is an object URL of its JPEG, released when it is removed or sent (and
   by `Page.js` when the card goes). A note shows its `images: [{id, url}]` as thumbnails under the
   text, one `Image.PreviewGroup` per note. Ctrl+Enter / ⌘+Enter saves, both here and in an inline edit; plain Enter and
   Shift+Enter stay line breaks, and an IME composition is never cut short.

   An inline edit shows the note's images as the composer's thumbnails, each with its ×: a removed
   one is hidden at once but goes to the API only with Save (`remove_images` beside `text`); Cancel
   or Esc brings them all back. Save is off when nothing changed, or when neither text nor an image
   would be left.

   Edit / Delete live in the note's row menu (`RowActions`, the Contacts and Addresses tabs' `⋮`),
   always shown, and only where the API says `can_edit` — the author's own note, any role; no role
   check here. It is hidden while the note is being edited. */

import React, { useEffect, useRef, useState } from "react";
import { Alert as Notice, Avatar, Button, Empty, Flex, Image, Input, Modal, Skeleton, Spin, Tooltip, Typography, Upload } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined, PaperClipOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { RowActions, fullName, initialsOf } from "../../component/table-cells";
import { API_URL_PREFIX } from "../../config/config";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, parseErrors } from "../../util/action";
import dayjs from "../../util/dayjs";
import { IMAGE_TYPES, blobToDataUri, checkImages, toJpeg } from "../../util/image-jpeg";
import { translate } from "../../util/util";

const BASE = "excalibur/comments/";
const MAX = 4000;
const toast = { position: "top-right" };
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const SHORTCUT_KEY = isMac ? "⌘" : "Ctrl";

/** `next` as the path `call()` takes. */
const relative = next => {
  if (!next) return null;
  if (next.startsWith(API_URL_PREFIX)) return next.slice(API_URL_PREFIX.length);
  const at = next.indexOf("/web/v1/");
  return at >= 0 ? next.slice(at + "/web/v1/".length) : next;
};

/** Ctrl/⌘+Enter → `action`; everything else (Enter, Shift+Enter, an IME composition) untouched. */
const saveKey = action => event => {
  if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
    event.preventDefault();
    action();
  }
};

const merge = (list, more) => {
  const seen = new Set(list.map(c => c.id));
  return [...list, ...more.filter(c => !seen.has(c.id))];
};

let imageSeq = 0;
const REFUSAL_KEY = { type: "cm_image_type", size: "cm_image_size", count: "cm_image_count", read: "cm_image_read" };

/** A note's images, the whole set in one preview (prev / next): one image larger, in its own
    proportions; two or more as a grid of equal squares. */
function NoteImages({ images, t }) {
  if (!images?.length) return null;
  return (
    <div className={`cp-cm__media cp-cm__media--${images.length === 1 ? "single" : "grid"}`}>
      <Image.PreviewGroup>
        {images.map((image, index) => (
          <Image
            key={image.id || image.url}
            src={image.url}
            rootClassName="cp-cm__media-item"
            className="cp-cm__media-img"
            alt={`${t("cm_image_alt")} ${index + 1}`}
          />
        ))}
      </Image.PreviewGroup>
    </div>
  );
}

/** One image as a square thumbnail with its × in the corner — the composer's draft and an inline
    edit alike. Rendered inside an `Image.PreviewGroup`. */
function Thumb({ url, index, onRemove, t }) {
  return (
    <div className="cp-cm__thumb-wrap">
      <Image src={url} rootClassName="cp-cm__thumb" className="cp-cm__thumb-img" alt={`${t("cm_image_alt")} ${index + 1}`} />
      <Button
        size="small"
        shape="circle"
        className="cp-cm__thumb-remove"
        icon={<CloseOutlined />}
        aria-label={`${t("cm_image_remove")} ${index + 1}`}
        title={t("cm_image_remove")}
        onClick={onRemove}
      />
    </div>
  );
}

/** Edited: `modified` later than `created` — as moments, not strings (both come with no zone). */
const isEdited = note => Boolean(note.created && note.modified) && dayjs(note.modified).isAfter(dayjs(note.created));

function dayLabel(created, t) {
  const day = dayjs(created).startOf("day");
  const today = dayjs().startOf("day");
  if (day.isSame(today)) return t("cm_today");
  if (day.isSame(today.subtract(1, "day"))) return t("cm_yesterday");
  return day.format("DD.MM.YYYY");
}

function Note({ note, t, onChanged, onRemoved, lang }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [busy, setBusy] = useState(false);
  /* Ids of the note's images removed in this edit — sent only with Save. */
  const [dropped, setDropped] = useState([]);
  const mayEdit = note.can_edit;
  const kept = (note.images || []).filter(image => !dropped.includes(image.id));
  const next = text.trim();
  /* Something changed, and text or an image is left — the rule the API no longer checks. */
  const canSave = (next !== note.text || dropped.length > 0) && (next !== "" || kept.length > 0);
  const name = fullName(note.author) || t("cm_unknown_author");
  const time = note.created ? dayjs(note.created).format("HH:mm") : "";

  const save = () => {
    if (!canSave || busy) return;
    setBusy(true);
    const post = { text: next, ...(dropped.length ? { remove_images: dropped } : {}) };
    call({ method: "PATCH", url: `${BASE}${note.id}/`, post }, response => {
      setBusy(false);
      if (response?.id) {
        onChanged(response);
        setDropped([]);
        setEditing(false);
        Alert.success(t("cm_updated_msg"), toast);
        return;
      }
      /* 403 / 404: `call()` hands a 404 `{detail}` over as data, a 403 logs out (handled there). */
      if (!response?.failed) Alert.warning(t(response?.detail ? "cm_forbidden_msg" : "edit_error_msg"), toast);
    });
  };
  const cancel = () => {
    setText(note.text);
    setDropped([]);
    setEditing(false);
  };

  /* Destructive, so confirmed — the start of the note in the question, the verb on the button. */
  const remove = () =>
    Modal.confirm({
      title: t("cm_delete_title"),
      content: note.text ? (note.text.length > 80 ? `${note.text.slice(0, 80)}…` : note.text) : undefined,
      okText: t("cm_delete"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `${BASE}${note.id}/` }, response => {
            resolve();
            if (response?.status === 204) {
              onRemoved(note.id);
              Alert.success(t("cm_deleted_msg"), toast);
            } else if (!response?.failed) Alert.warning(t(response?.detail ? "cm_forbidden_msg" : "edit_error_msg"), toast);
          })
        ),
    });

  return (
    <li className="cp-cm__item" data-id={note.id}>
      <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }} src={note.author?.photo || null}>
        {initialsOf(fullName(note.author)) || "?"}
      </Avatar>
      <div className="cp-cm__body">
        <Flex className="cp-cm__meta" align="center" gap={6} wrap>
          <Typography.Text strong className="cp-cm__author">
            {name}
          </Typography.Text>
          <span className="cp-cm__time lp-num">{time}</span>
          {isEdited(note) ? <span className="cp-cm__time">· {t("cm_edited")}</span> : null}
          {mayEdit && !editing ? (
            <span className="cp-cm__actions">
              {/* Every note has the same menu: its accessible name says whose note, and when. */}
              <RowActions
                label={`${t("cm_actions")}: ${name}, ${time}`}
                items={[
                  { key: "edit", label: t("cm_edit"), icon: <EditOutlined /> },
                  { key: "delete", label: t("cm_delete"), icon: <DeleteOutlined />, danger: true },
                ]}
                onClick={key => (key === "edit" ? setEditing(true) : remove())}
              />
            </span>
          ) : null}
        </Flex>
        {editing ? (
          <Flex vertical gap={8} className="cp-cm__edit">
            <Input.TextArea
              autoFocus
              autoSize={{ minRows: 2, maxRows: 10 }}
              maxLength={MAX}
              showCount
              value={text}
              aria-label={t("cm_edit")}
              onChange={e => setText(e.target.value)}
              onKeyDown={event => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancel();
                  return;
                }
                saveKey(save)(event);
              }}
            />
            {kept.length ? (
              <Flex wrap gap={8}>
                <Image.PreviewGroup>
                  {kept.map((image, index) => (
                    <Thumb key={image.id} url={image.url} index={index} t={t} onRemove={() => setDropped(prev => [...prev, image.id])} />
                  ))}
                </Image.PreviewGroup>
              </Flex>
            ) : null}
            <Flex gap={8} justify="flex-end">
              <Button onClick={cancel}>{t("cm_cancel")}</Button>
              <Button type="primary" loading={busy} disabled={!canSave} onClick={save}>
                {t("cm_save")}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <>
            {note.text ? <div className="cp-cm__text">{note.text}</div> : null}
            <NoteImages images={note.images} t={t} />
          </>
        )}
      </div>
    </li>
  );
}

export default function CommentsTab({ counterpartyId, lang, draft, setDraft, images, setImages, onCountsChange }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftError, setDraftError] = useState(false);
  /* The draft's images: files refused with their reason, how many are still being re-encoded,
     whether one is being dragged over the composer, and a 400 on `images`. */
  const [refused, setRefused] = useState([]);
  const [encoding, setEncoding] = useState(0);
  const encodingRef = useRef(0);
  const [dropping, setDropping] = useState(false);
  const [imagesError, setImagesError] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setError(false);
    setItems(null);
    /* Never without the counterparty: the API would answer every comment of the company. */
    if (!counterpartyId) return;
    call({ method: "GET", url: `${BASE}?counterparty=${counterpartyId}` }, response => {
      if (mine !== seq.current) return;
      if (!response || response.failed || !Array.isArray(response.results)) {
        setItems([]);
        setError(true);
        return;
      }
      setItems(response.results);
      setNext(relative(response.next));
    });
  }, [counterpartyId, reload]);

  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(false);
    call({ method: "GET", url: next }, response => {
      setLoadingMore(false);
      if (!response || response.failed || !Array.isArray(response.results)) {
        setMoreError(true);
        return;
      }
      setItems(prev => merge(prev || [], response.results));
      setNext(relative(response.next));
    });
  };

  /* A pasted screenshot has no name of its own worth showing. */
  const nameOf = file => (file.name && file.name !== "image.png" ? file.name : t("cm_image_pasted"));
  const addFiles = files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const { accepted, rejected } = checkImages(list, images.length + encodingRef.current);
    setRefused(rejected.map(r => ({ name: nameOf(r.file), reason: r.reason })));
    setImagesError(false);
    if (!accepted.length) return;
    encodingRef.current += accepted.length;
    setEncoding(encodingRef.current);
    /* Added together and in the order picked, once every one of them is re-encoded. */
    Promise.allSettled(accepted.map(toJpeg)).then(results => {
      encodingRef.current -= accepted.length;
      setEncoding(encodingRef.current);
      const added = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") added.push({ key: `img${++imageSeq}`, blob: result.value, url: URL.createObjectURL(result.value) });
        else setRefused(prev => [...prev, { name: nameOf(accepted[index]), reason: "read" }]);
      });
      if (added.length) setImages(prev => [...prev, ...added]);
    });
  };
  const removeImage = key => {
    const gone = images.find(image => image.key === key);
    if (gone) URL.revokeObjectURL(gone.url);
    setImages(prev => prev.filter(image => image.key !== key));
    setImagesError(false);
  };
  /* Only a drag that carries files is a drop target; dragging selected text is left alone. */
  const carriesFiles = event => Array.from(event.dataTransfer?.types || []).includes("Files");
  const dropProps = {
    onDragOver: event => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (!dropping) setDropping(true);
    },
    onDragLeave: event => {
      if (!event.currentTarget.contains(event.relatedTarget)) setDropping(false);
    },
    onDrop: event => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDropping(false);
      addFiles(event.dataTransfer.files);
    },
  };
  /* A paste that carries an image adds it; any other paste is the textarea's own. */
  const onPaste = event => {
    const files = Array.from(event.clipboardData?.files || []);
    if (!files.some(file => file.type.startsWith("image/"))) return;
    event.preventDefault();
    addFiles(files);
  };

  const text = draft.trim();
  const create = async () => {
    /* Text or at least one image — the API keeps a comment that has either; `text` goes as it is,
       "" included. */
    if ((!text && !images.length) || busy || encoding) return;
    setBusy(true);
    setDraftError(false);
    setImagesError(false);
    let uris = [];
    try {
      uris = await Promise.all(images.map(image => blobToDataUri(image.blob)));
    } catch (e) {
      setBusy(false);
      setImagesError(true);
      return;
    }
    const post = { counterparty: counterpartyId, text, ...(uris.length ? { images: uris.map(image => ({ image })) } : {}) };
    call({ method: "POST", url: BASE, post }, response => {
      setBusy(false);
      if (response?.id) {
        setItems(prev => [response, ...(prev || []).filter(c => c.id !== response.id)]);
        setDraft("");
        images.forEach(image => URL.revokeObjectURL(image.url));
        setImages([]);
        setRefused([]);
        Alert.success(t("cm_saved_msg"), toast);
        onCountsChange?.();
        return;
      }
      if (typeof response?.json !== "function") return; // a 5xx / network failure — `call()` toasted it
      /* A 400 names its field: `text` is the draft's own (marked under it), `counterparty` means
         the card's counterparty is gone or not the company's — nothing the draft can fix. */
      parseErrors(response).then(parsed => {
        const keys = Object.keys(parsed.fields);
        if (keys.includes("text")) setDraftError(true);
        /* `images` comes back per item (`[{image: [...]}]`); one general line under the composer. */
        if (keys.includes("images")) setImagesError(true);
        if (keys.includes("counterparty")) Alert.warning(t("card_not_found_title"), toast);
        else if (!keys.includes("text") && !keys.includes("images")) Alert.warning(t("edit_error_msg"), toast);
      });
    });
  };

  const changed = row => setItems(prev => prev.map(c => (c.id === row.id ? row : c)));
  const removed = id => {
    setItems(prev => prev.filter(c => c.id !== id));
    onCountsChange?.();
  };

  /* Day groups over the loaded rows, in order. */
  const groups = [];
  (items || []).forEach(note => {
    const label = dayLabel(note.created, t);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.notes.push(note);
    else groups.push({ label, notes: [note] });
  });

  return (
    <section className="cp-cm" aria-label={t("tab_comments")}>
      <div className="cp-cm__composer">
        {/* One box: the borderless field, the draft's images, the action row. The box takes the
            border, the hover and the focus ring (`:focus-within`), and is the drop target. */}
        <div className={`cp-cm__box${dropping ? " cp-cm__box--drop" : ""}`} {...dropProps}>
          <Input.TextArea
            id="cp-cm-new"
            variant="borderless"
            className="cp-cm__field"
            autoSize={{ minRows: 2, maxRows: 10 }}
            maxLength={MAX}
            value={draft}
            placeholder={t("cm_placeholder")}
            aria-label={t("cm_placeholder")}
            aria-invalid={draftError || undefined}
            aria-describedby={draftError ? "cp-cm-new-error" : undefined}
            status={draftError ? "error" : undefined}
            onChange={e => {
              setDraft(e.target.value);
              setDraftError(false);
            }}
            onKeyDown={saveKey(create)}
            onPaste={onPaste}
          />
          {images.length || encoding ? (
            <Flex wrap gap={8} className="cp-cm__thumbs">
              <Image.PreviewGroup>
                {images.map((image, index) => (
                  <Thumb key={image.key} url={image.url} index={index} t={t} onRemove={() => removeImage(image.key)} />
                ))}
              </Image.PreviewGroup>
              {Array.from({ length: encoding }, (v, index) => (
                <span key={`enc${index}`} className="cp-cm__thumb-pending">
                  <Spin size="small" />
                </span>
              ))}
            </Flex>
          ) : null}
          <Flex className="cp-cm__bar" align="center" justify="space-between" gap="small" wrap>
            {/* `beforeUpload` runs once per file; the first call takes the whole pick. Nothing is
              uploaded by antd — the files go through the same check as a drop or a paste. The
              limits live in the clip's tooltip (and in the drop layer), not on the page. */}
            <Upload
              accept={IMAGE_TYPES.join(",")}
              multiple
              showUploadList={false}
              beforeUpload={(file, fileList) => {
                if (file === fileList[0]) addFiles(fileList);
                return false;
              }}>
              <Tooltip title={t("cm_images_hint")}>
                <Button type="text" icon={<PaperClipOutlined />} aria-label={t("cm_attach")} />
              </Tooltip>
            </Upload>
            <Flex align="center" gap="small" className="cp-cm__composer-actions">
              <span className="cp-cm__count lp-num">
                {draft.length} / {MAX}
              </span>
              <span className="cp-cm__shortcut">{t("cm_shortcut").replace("{key}", SHORTCUT_KEY)}</span>
              <Button type="primary" loading={busy} disabled={(!text && !images.length) || encoding > 0} onClick={create}>
                {t("cm_save")}
              </Button>
            </Flex>
          </Flex>
          {dropping ? (
            <div className="cp-cm__drop" aria-hidden>
              <span className="cp-cm__drop-title">{t("cm_drop")}</span>
              <span className="cp-cm__hint">{t("cm_images_hint")}</span>
            </div>
          ) : null}
        </div>
        <Typography.Text type="secondary" className="cp-cm__hint cp-cm__note">
          {t("cm_hint")}
        </Typography.Text>
        {draftError ? (
          <Typography.Text type="danger" className="cp-cm__hint" id="cp-cm-new-error">
            {t("ad_field_invalid")}
          </Typography.Text>
        ) : null}
        <Flex vertical aria-live="polite">
          {refused.map((r, index) => (
            <Typography.Text key={`${r.name}-${index}`} type="danger" className="cp-cm__hint">
              {t(REFUSAL_KEY[r.reason]).replace("{name}", r.name)}
            </Typography.Text>
          ))}
          {imagesError ? (
            <Typography.Text type="danger" className="cp-cm__hint">
              {t("cm_images_invalid")}
            </Typography.Text>
          ) : null}
        </Flex>
      </div>

      {items === null ? (
        <Skeleton active avatar paragraph={{ rows: 3 }} />
      ) : error ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{t("tab_load_error")}</Typography.Text>
              <Button size="small" onClick={() => setReload(k => k + 1)}>
                {t("retry")}
              </Button>
            </Flex>
          }
        />
      ) : !items.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("cm_empty")} />
      ) : (
        <>
          {groups.map(group => (
            <div key={group.label} className="cp-cm__day">
              {/* Not `role="separator"`: that role hides its content, and the day is the point. The
                  rules either side are pseudo-elements. */}
              <div className="cp-cm__divider">
                <span>{group.label}</span>
              </div>
              <ul className="cp-cm__list">
                {group.notes.map(note => (
                  <Note key={note.id} note={note} t={t} lang={lang} onChanged={changed} onRemoved={removed} />
                ))}
              </ul>
            </div>
          ))}
          {moreError ? (
            <Notice
              type="error"
              showIcon
              className="cp-cm__more-error"
              title={t("cm_more_error")}
              action={
                <Button size="small" onClick={loadMore}>
                  {t("retry")}
                </Button>
              }
            />
          ) : null}
          {next && !moreError ? (
            <Flex justify="center" className="cp-cm__more">
              <Button loading={loadingMore} onClick={loadMore}>
                {t("cm_more")}
              </Button>
            </Flex>
          ) : null}
        </>
      )}
    </section>
  );
}
